/* ============================================================
   DS Store Locator – Web Component
   DSGVO: Zero external requests until consent
   A11y: WCAG 2.1 AA compliant
   ============================================================ */

if (!window.customElements.get('ds-store-locator')) {
  // Fallback, falls das data-strings-Script fehlt (z.B. älteres gecachtes Section-Markup)
  const DEFAULT_STRINGS = {
    resultsCountZero: 'Keine Standorte gefunden',
    resultsCountOne: '1 Standort gefunden',
    resultsCountOther: '[count] Standorte gefunden',
    noResults: 'Keine Standorte in der Nähe gefunden. Versuche einen größeren Umkreis.',
    noResultsInRadius: 'Keine Händler im Umkreis von [radius] km um [location]. Die nächsten Standorte:',
    resultsInRadius: 'Händler im Umkreis von [radius] km um [location]',
    distanceAway: '[count] km entfernt',
    openingHours: 'Öffnungszeiten',
    closed: 'Geschlossen',
    website: 'Website',
    email: 'E-Mail',
    availableProducts: 'Verfügbare Produkte',
    products: 'Produkte',
    locationNotFound: 'Keinen passenden Ort gefunden. Prüfe die Schreibweise oder suche direkt nach dem Händlernamen.',
    searching: 'Suche läuft …',
    nearYou: 'deinen Standort',
    geolocateError: 'Dein Standort konnte nicht ermittelt werden. Bitte erlaube die Standortfreigabe im Browser.',
    suggestDealers: 'Händler',
    suggestCities: 'Orte',
    route: 'Route planen',
    postalRegionHint: 'Händler im PLZ-Gebiet [plz]',
    filterAll: 'Alle',
    filterAllProducts: 'Alle Produkte',
    closedKeywords: 'geschlossen',
  };

  // Ländernamen (wie im Metaobject gepflegt, DE/EN/Landessprache) → ISO-Codes für Nominatim
  const COUNTRY_CODES = {
    'deutschland': 'de', 'germany': 'de', 'de': 'de',
    'osterreich': 'at', 'austria': 'at', 'at': 'at',
    'schweiz': 'ch', 'switzerland': 'ch', 'suisse': 'ch', 'ch': 'ch',
    'niederlande': 'nl', 'netherlands': 'nl', 'nederland': 'nl', 'holland': 'nl', 'nl': 'nl',
    'belgien': 'be', 'belgium': 'be', 'belgie': 'be', 'belgique': 'be', 'be': 'be',
    'frankreich': 'fr', 'france': 'fr', 'fr': 'fr',
    'luxemburg': 'lu', 'luxembourg': 'lu', 'lu': 'lu',
    'danemark': 'dk', 'denmark': 'dk', 'danmark': 'dk', 'dk': 'dk',
    'schweden': 'se', 'sweden': 'se', 'sverige': 'se', 'se': 'se',
    'norwegen': 'no', 'norway': 'no', 'norge': 'no',
    'finnland': 'fi', 'finland': 'fi',
    'italien': 'it', 'italy': 'it', 'italia': 'it', 'it': 'it',
    'spanien': 'es', 'spain': 'es', 'espana': 'es', 'es': 'es',
    'polen': 'pl', 'poland': 'pl', 'polska': 'pl', 'pl': 'pl',
    'tschechien': 'cz', 'czechia': 'cz', 'czech republic': 'cz',
    'grossbritannien': 'gb', 'vereinigtes konigreich': 'gb', 'united kingdom': 'gb', 'uk': 'gb', 'gb': 'gb', 'england': 'gb',
    'irland': 'ie', 'ireland': 'ie',
    'kanada': 'ca', 'canada': 'ca', 'ca': 'ca',
    'usa': 'us', 'united states': 'us', 'vereinigte staaten': 'us',
  };

  // Händler eines Orts dürfen maximal so weit vom gemeinsamen Schwerpunkt
  // liegen, damit ihre Koordinaten als Mittelpunkt der Umkreissuche taugen
  const LOCAL_PLACE_MAX_SPREAD_KM = 30;

  class DSStoreLocator extends HTMLElement {
    constructor() {
      super();
      this.map = null;
      this.markers = [];
      this.locations = [];
      this.filteredLocations = [];
      this.activeFilter = 'all';
      this.activeProduct = 'all';
      this.searchCache = {};
      this._lastRadiusSearch = null;
      this._suggestIndex = [];
      this._suggestItems = [];
      this._suggestActive = -1;
    }

    connectedCallback() {
      const dataEl = this.querySelector('[data-locations]');
      if (dataEl) {
        try {
          this.locations = JSON.parse(dataEl.textContent);
        } catch (e) {
          this.locations = [];
        }
      }

      // Shop-Sprache für Sortierung/Kollation (nicht hart auf eine Sprache verdrahten)
      this._locale = (document.documentElement.lang || 'de').split('-')[0];
      this.locations.sort((a, b) => (a.name || '').localeCompare(b.name || '', this._locale));

      this.loadStrings();
      this.filteredLocations = [...this.locations];
      this.buildSuggestIndex();
      this.bindEvents();
      this.renderList();
      this.updateStatus();
      this.buildFilterChips();
      this.buildProductChips();
      this.updateFilterbar();
      this.updateFilterReset();

      // Consent bereits erteilt → Karte direkt laden
      if (this.hasConsent()) {
        this.loadMap();
      }
    }

    // --- Übersetzte UI-Strings laden (aus data-strings, per | t im Liquid befüllt) ---
    loadStrings() {
      const stringsEl = this.querySelector('[data-strings]');
      let parsed = {};
      if (stringsEl) {
        try { parsed = JSON.parse(stringsEl.textContent); } catch (e) { parsed = {}; }
      }

      // Fehlende Locale-Keys liefern "Translation missing: …" als Wert –
      // solche Einträge verwerfen, damit der Fallback greift statt des Fehlertexts
      Object.keys(parsed).forEach(key => {
        const value = parsed[key];
        if (typeof value !== 'string' || value.indexOf('Translation missing') === 0 || value === '') {
          delete parsed[key];
        }
      });

      this.strings = Object.assign({}, DEFAULT_STRINGS, parsed);
    }

    // --- Platzhalter in übersetzten Strings ersetzen.
    //     [count]-Syntax ist der Standard (Theme-Editor-sicher);
    //     {{ count }} bleibt als Legacy-Syntax unterstützt ---
    formatString(template, vars) {
      return String(template)
        .replace(/\[(\w+)\]/g, (match, key) =>
          vars && key in vars ? vars[key] : match
        )
        .replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) =>
          vars && key in vars ? vars[key] : match
        );
    }

    // --- Consent prüfen / speichern ---
    hasConsent() {
      try { return localStorage.getItem('ds-map-consent') === '1'; } catch (e) { return false; }
    }

    saveConsent() {
      try { localStorage.setItem('ds-map-consent', '1'); } catch (e) {}
    }

    // --- Event Binding ---
    bindEvents() {
      const consentBtn = this.querySelector('[data-consent-btn]');
      if (consentBtn) {
        consentBtn.addEventListener('click', () => this.loadMap());
      }

      const searchBtn = this.querySelector('[data-search-btn]');
      if (searchBtn) {
        searchBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleSearch();
        });
      }

      const searchInput = this.querySelector('[data-search-input]');
      if (searchInput) {
        const clearBtn = this.querySelector('[data-search-clear]');
        this._updateClearBtn = () => { if (clearBtn) clearBtn.hidden = !searchInput.value; };
        this._updateClearBtn();

        searchInput.addEventListener('input', () => {
          this._updateClearBtn();
          if (this._suppressSuggest) {
            this._suppressSuggest = false;
            return;
          }
          this.renderSuggestions(searchInput.value);
        });

        // ARIA-Combobox-Pattern: Pfeiltasten steuern die Vorschläge,
        // Enter übernimmt die aktive Option oder startet die Suche
        searchInput.addEventListener('keydown', (e) => {
          const open = this.isSuggestOpen();

          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!open) {
              this.renderSuggestions(searchInput.value);
              if (!this.isSuggestOpen()) return;
            }
            const delta = e.key === 'ArrowDown' ? 1 : -1;
            const count = this._suggestItems.length;
            const next = this._suggestActive < 0
              ? (delta === 1 ? 0 : count - 1)
              : (this._suggestActive + delta + count) % count;
            this.setActiveSuggestion(next);
            return;
          }

          if (e.key === 'Enter') {
            e.preventDefault();
            if (open && this._suggestActive >= 0) {
              this.selectSuggestion(this._suggestActive);
            } else {
              this.closeSuggestions();
              this.handleSearch();
            }
            return;
          }

          if (e.key === 'Escape' && open) {
            e.stopPropagation();
            this.closeSuggestions();
            return;
          }

          if (e.key === 'Tab') this.closeSuggestions();
        });

        searchInput.addEventListener('blur', () => this.closeSuggestions());

        // Natives Leeren des Suchfelds (z.B. Escape in Safari) setzt die Liste zurück
        searchInput.addEventListener('search', () => {
          if (!searchInput.value) this.handleSearch();
        });

        if (clearBtn) {
          clearBtn.addEventListener('click', () => {
            searchInput.value = '';
            this._updateClearBtn();
            this.closeSuggestions();
            this.handleSearch();
            searchInput.focus();
          });
        }

        const suggest = this.querySelector('[data-suggest]');
        if (suggest) {
          // mousedown abfangen, damit das Input den Fokus behält (kein blur vor click)
          suggest.addEventListener('mousedown', (e) => e.preventDefault());
          suggest.addEventListener('click', (e) => {
            const option = e.target.closest('[data-suggest-index]');
            if (option) this.selectSuggestion(parseInt(option.dataset.suggestIndex, 10));
          });
          // Hover und Tastatur teilen sich eine einzige aktive Hervorhebung
          suggest.addEventListener('mousemove', (e) => {
            const option = e.target.closest('[data-suggest-index]');
            if (option) {
              const index = parseInt(option.dataset.suggestIndex, 10);
              if (index !== this._suggestActive) this.setActiveSuggestion(index, { scroll: false });
            }
          });
        }
      }

      const geolocateBtn = this.querySelector('[data-geolocate-btn]');
      if (geolocateBtn) {
        geolocateBtn.addEventListener('click', () => this.handleGeolocate());
      }

      const radiusSelect = this.querySelector('[data-radius-select]');
      if (radiusSelect) {
        radiusSelect.addEventListener('change', () => this.handleRadiusChange());
      }

      const resetBtn = this.querySelector('[data-filter-reset]');
      if (resetBtn) {
        resetBtn.addEventListener('click', () => this.resetFilters());
      }

      this.setupDropdownBehavior();
    }

    // --- DSGVO Consent + Leaflet laden ---
    async loadMap() {
      const consent = this.querySelector('[data-consent]');
      this.saveConsent();

      // Leaflet CSS laden
      if (!document.querySelector('link[href*="leaflet"]')) {
        const css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        css.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
        css.crossOrigin = '';
        document.head.appendChild(css);
      }

      // Leaflet JS laden
      if (!window.L) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
          script.crossOrigin = '';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      // Consent-Overlay ausblenden
      if (consent) consent.hidden = true;

      this.initMap();
    }

    // --- Karte initialisieren ---
    initMap() {
      const mapEl = this.querySelector('[data-map]');
      if (!mapEl || !window.L) return;

      // Karte zentrieren auf Deutschland
      this.map = L.map(mapEl, {
        zoomControl: true,
        scrollWheelZoom: false,
        keyboard: true,
      }).setView([51.1657, 10.4515], 6);

      // "Leaflet"-Prefix in der Attribution ausblenden (OSM-Hinweis bleibt erhalten)
      this.map.attributionControl.setPrefix(false);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 18,
      }).addTo(this.map);

      // Scroll-Zoom erst nach Klick aktivieren
      this.map.on('click', () => this.map.scrollWheelZoom.enable());
      this.map.on('mouseout', () => this.map.scrollWheelZoom.disable());

      this.addMarkers(this.filteredLocations);

      // Statt Deutschland-/Weltansicht: ersten Standort direkt fokussieren
      // (Übersichts-Zoom – Straßenebene erst bei expliziter Auswahl)
      this.highlightLocation(0, { animate: false, zoom: 13 });
    }

    // --- Eigener SVG-Pin (Akzentfarbe, retina-scharf, kein externes Bild) ---
    markerIcon(active) {
      return L.divIcon({
        className: 'ds-store-locator__marker' + (active ? ' is-active' : ''),
        html: '<svg viewBox="0 0 30 40" aria-hidden="true"><path d="M15 0C6.72 0 0 6.72 0 15c0 10.4 15 25 15 25s15-14.6 15-25C30 6.72 23.28 0 15 0z" fill="currentColor"/><circle cx="15" cy="15" r="5.5" fill="#fff"/></svg>',
        iconSize: [30, 40],
        iconAnchor: [15, 40],
        popupAnchor: [0, -42],
      });
    }

    // --- Koordinate parsen (toleriert deutsches Komma als Dezimaltrenner) ---
    parseCoord(value) {
      return parseFloat(String(value == null ? '' : value).trim().replace(',', '.'));
    }

    // --- Koordinaten validieren (0/0 = nicht gepflegter Metaobject-Default,
    //     Werte außerhalb ±90/±180 = Tippfehler wie fehlender Dezimalpunkt) ---
    hasValidCoords(lat, lng) {
      return Number.isFinite(lat) && Number.isFinite(lng) &&
        Math.abs(lat) <= 90 && Math.abs(lng) <= 180 &&
        !(lat === 0 && lng === 0);
    }

    // --- Marker setzen ---
    addMarkers(locations) {
      if (!this.map) return;

      // Alte Marker entfernen
      this.markers.forEach(m => { if (m) this.map.removeLayer(m); });
      this.markers = [];
      this._activeMarkerIndex = null;

      locations.forEach((loc, i) => {
        const lat = this.parseCoord(loc.latitude);
        const lng = this.parseCoord(loc.longitude);

        // Ungültige Koordinaten bekommen keinen Marker, behalten aber ihren
        // Index, damit Liste und Karte synchron bleiben
        if (!this.hasValidCoords(lat, lng)) {
          this.markers.push(null);
          return;
        }

        const marker = L.marker([lat, lng], {
          icon: this.markerIcon(false),
          riseOnHover: true,
          title: loc.name,
        }).addTo(this.map);

        // autoPan aus: die Zentrierung übernimmt highlightLocation()
        marker.bindPopup(this.buildPopup(loc, lat, lng), { autoPan: false });
        marker._locationData = loc;

        marker.on('click', () => this.highlightLocation(i, { scrollList: true }));

        this.markers.push(marker);
      });
    }

    // --- Popup-Inhalt: Adresse + Aktionszeile (Telefon, Website, Route) ---
    buildPopup(loc, lat, lng) {
      let html = `<strong>${this.escapeHtml(loc.name)}</strong>`;
      html += `${this.escapeHtml(loc.address)}<br>${this.escapeHtml(loc.postal_code)} ${this.escapeHtml(loc.city)}`;
      if (loc.country) html += `<br>${this.escapeHtml(loc.country)}`;

      const actions = [];
      if (loc.phone) actions.push(`<a href="tel:${this.escapeHtml(loc.phone)}">${this.escapeHtml(loc.phone)}</a>`);
      if (loc.website) actions.push(`<a href="${this.escapeHtml(loc.website)}" target="_blank" rel="noopener">${this.escapeHtml(this.strings.website)}</a>`);
      actions.push(`<a href="${this.routeUrl(lat, lng)}" target="_blank" rel="noopener">${this.escapeHtml(this.strings.route)}</a>`);

      html += `<span class="ds-store-locator__popup-actions">${actions.join('')}</span>`;
      return html;
    }

    // --- Routen-Link (öffnet Google Maps / auf Mobile die native Karten-App) ---
    routeUrl(lat, lng) {
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${lat},${lng}`)}`;
    }

    // --- Text normalisieren (Kleinschreibung, Umlaute/Akzente entfernen) ---
    normalizeText(str) {
      return String(str || '')
        .toLowerCase()
        .replace(/ß/g, 'ss')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
    }

    // --- Aktuell gewählter Umkreis in km ---
    currentRadius() {
      const select = this.querySelector('[data-radius-select]');
      return parseInt(select?.value || '50', 10);
    }

    // --- Suche: eigene Händlerdaten zuerst, Geocoding nur als zweite Stufe.
    //     intent kommt aus der Vorschlagsliste ('dealer' | 'place') und legt die
    //     Deutung fest. Ohne intent entscheidet die Eingabe selbst: ein Ortsname
    //     gewinnt gegen Händlernamen, die den Ort nur enthalten – „Köln“ ist eine
    //     Umkreissuche um Köln, nicht bloß der Treffer „Globetrotter Köln“ ---
    async handleSearch(options = {}) {
      const { intent = null } = options;
      const input = this.querySelector('[data-search-input]');
      const query = input?.value.trim();
      this.closeSuggestions();
      this._searchHint = null;
      this._emptyMessage = null;
      this._lastRadiusSearch = null;

      // Leere Suche → alle anzeigen, erster Standort fokussiert
      if (!query) {
        this.filteredLocations = this.applyFilters(this.locations);
        this.filteredLocations.forEach(loc => delete loc._distance);
        this.renderList();
        this.updateStatus();
        if (this.map) {
          this.map.invalidateSize();
          this.addMarkers(this.filteredLocations);
        }
        this.highlightLocation(0);
        return;
      }

      const radius = this.currentRadius();
      const q = this.normalizeText(query);
      const qCompact = q.replace(/[\s-]+/g, '');
      const hasDigit = /\d/.test(query);

      // Kandidaten aus den eigenen Daten – ungefiltert, damit Typ-/Produktfilter
      // nur die Treffer einschränken, nicht die Deutung der Eingabe ändern
      const nameMatches = intent === 'place'
        ? []
        : this.locations.filter(loc => this.normalizeText(loc.name).includes(q));
      const cityMatches = hasDigit || intent === 'dealer'
        ? []
        : this.locations.filter(loc => this.normalizeText(loc.city).includes(q));
      const cityExact = cityMatches.filter(loc => this.normalizeText(loc.city) === q);

      // 1) Ort → Umkreissuche. Gilt bei expliziter Ortswahl, exaktem Ortsnamen
      //    oder wenn kein Händlername passt (Teil-Eingabe wie „Biele“)
      const isPlace = cityMatches.length > 0 &&
        (intent === 'place' || cityExact.length > 0 || nameMatches.length === 0);
      if (isPlace) {
        await this.searchPlace(query, cityExact.length > 0 ? cityExact : cityMatches, radius);
        this.focusStatus();
        return;
      }

      // 2) Händlername direkt in den eigenen Daten –
      //    verhindert, dass Nominatim Namen zu falschen Orten geocodet
      if (nameMatches.length > 0) {
        this.showResults(this.applyFilters(nameMatches));
        this.focusStatus();
        return;
      }

      // PLZ-Treffer aus den eigenen Daten – werden mit der Umkreissuche
      // zusammengeführt (findet Händler auch bei fehlerhaften Koordinaten).
      // Reine Ziffernfolgen schon ab 2 Stellen (PLZ-Leitregion, z.B. "44")
      const isDigits = /^\d+$/.test(qCompact);
      const postalMatches = hasDigit && qCompact.length >= (isDigits ? 2 : 3)
        ? this.applyFilters(
            this.locations.filter(loc =>
              this.normalizeText(loc.postal_code).replace(/[\s-]+/g, '').startsWith(qCompact)
            )
          ).sort((a, b) => String(a.postal_code).localeCompare(String(b.postal_code), this._locale))
        : [];

      // 2–3 Ziffern = PLZ-Gebiet: deterministisch aus den eigenen Daten
      // beantworten – Geocoding wäre bei "44" nur Rätselraten
      if (isDigits && qCompact.length <= 3) {
        if (postalMatches.length > 0) {
          this._searchHint = this.formatString(this.strings.postalRegionHint, { plz: qCompact });
          this.showResults(postalMatches);
        } else {
          this.showEmpty(this.strings.locationNotFound);
        }
        this.focusStatus();
        return;
      }

      // 3) Geocoding via Nominatim, eingegrenzt auf Länder mit Händlern
      await this.searchByGeocoding(query, radius, postalMatches);
      this.focusStatus();
    }

    // --- Umkreissuche um einen Ort aus den eigenen Daten: Mittelpunkt ist der
    //     Schwerpunkt der dortigen Händler (kein externer Request nötig) ---
    async searchPlace(query, matches, radius) {
      const center = this.localPlaceCenter(matches);
      if (center) {
        this.applyRadiusSearch({ lat: center.lat, lng: center.lng, name: matches[0].city }, radius, matches);
        return;
      }

      // Treffer liegen weit auseinander (mehrere Orte, z.B. „Saint“) → Liste ohne Umkreis
      const anyCoords = matches.some(loc =>
        this.hasValidCoords(this.parseCoord(loc.latitude), this.parseCoord(loc.longitude))
      );
      if (anyCoords) {
        this.showResults(this.applyFilters(matches));
        return;
      }

      // Nur Händler ohne brauchbare Koordinaten → Ort extern geocoden
      await this.searchByGeocoding(query, radius, matches);
    }

    // --- Schwerpunkt mehrerer Standorte; null, wenn Koordinaten fehlen oder die
    //     Streuung zu groß ist, um als ein Ort durchzugehen ---
    localPlaceCenter(locations) {
      const points = [];
      locations.forEach(loc => {
        const lat = this.parseCoord(loc.latitude);
        const lng = this.parseCoord(loc.longitude);
        if (this.hasValidCoords(lat, lng)) points.push([lat, lng]);
      });
      if (points.length === 0) return null;

      const lat = points.reduce((sum, p) => sum + p[0], 0) / points.length;
      const lng = points.reduce((sum, p) => sum + p[1], 0) / points.length;
      const spread = Math.max(...points.map(p => this.haversine(lat, lng, p[0], p[1])));
      return spread <= LOCAL_PLACE_MAX_SPREAD_KM ? { lat, lng } : null;
    }

    // --- Geocoding + Umkreissuche; extra = lokale Treffer, die immer mitkommen ---
    async searchByGeocoding(query, radius, extra) {
      this.setSearchBusy(true);
      let coords = null;
      try {
        coords = await this.geocode(query);
      } finally {
        this.setSearchBusy(false);
      }

      if (coords) {
        this.applyRadiusSearch({ lat: coords.lat, lng: coords.lng, name: coords.name || query }, radius, extra);
      } else if (extra.length > 0) {
        this.showResults(this.applyFilters(extra));
      } else {
        this.showEmpty(this.strings.locationNotFound);
      }
    }

    // --- Umkreis geändert → laufende Umkreissuche mit neuem Radius wiederholen
    //     (Mittelpunkt bleibt, kein erneutes Geocoding) ---
    handleRadiusChange() {
      const last = this._lastRadiusSearch;
      if (!last) return;
      this.applyRadiusSearch(last.center, this.currentRadius(), last.extra);
    }

    // --- Leerer Zustand mit Meldung ---
    showEmpty(message) {
      this.filteredLocations = [];
      this._emptyMessage = message;
      this.renderList();
      this.updateStatus();
      if (this.map) {
        this.map.invalidateSize();
        this.addMarkers([]);
      }
    }

    // --- Treffer aus den eigenen Daten anzeigen (ohne Umkreis) ---
    showResults(locations) {
      this.filteredLocations = locations.map(loc => {
        const copy = { ...loc };
        delete copy._distance;
        return copy;
      });
      this.renderList();
      this.updateStatus();

      if (!this.map) return;
      this.map.invalidateSize();
      this.addMarkers(this.filteredLocations);

      if (this.filteredLocations.length === 1) {
        this.highlightLocation(0);
        return;
      }

      const bounds = [];
      this.filteredLocations.forEach(loc => {
        const lat = this.parseCoord(loc.latitude);
        const lng = this.parseCoord(loc.longitude);
        if (this.hasValidCoords(lat, lng)) bounds.push([lat, lng]);
      });
      if (bounds.length > 0) {
        this.map.closePopup();
        this.map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12, animate: true });
      }
    }

    // --- Umkreissuche um einen Punkt (Geocoding-Treffer oder Geolocation) ---
    applyRadiusSearch(coords, radius, extra = []) {
      this._lastRadiusSearch = { center: coords, extra };

      const keyOf = (loc) => `${loc.name}|${loc.postal_code}|${loc.city}`;
      const extraKeys = new Set(extra.map(keyOf));

      const allWithDistance = this.applyFilters(this.locations)
        .map(loc => {
          const lat = this.parseCoord(loc.latitude);
          const lng = this.parseCoord(loc.longitude);
          return {
            ...loc,
            _distance: this.hasValidCoords(lat, lng)
              ? this.haversine(coords.lat, coords.lng, lat, lng)
              : Infinity,
          };
        })
        .sort((a, b) => a._distance - b._distance);

      const inRadius = allWithDistance.filter(loc =>
        loc._distance <= radius || extraKeys.has(keyOf(loc))
      );

      if (inRadius.length > 0) {
        this.filteredLocations = inRadius;
        this._searchHint = this.formatString(this.strings.resultsInRadius, {
          radius,
          location: coords.name,
        });
      } else {
        // Keine Treffer im Radius → nächste 3 Standorte anzeigen
        this.filteredLocations = allWithDistance
          .filter(loc => Number.isFinite(loc._distance))
          .slice(0, 3);
        if (this.filteredLocations.length > 0) {
          this._searchHint = this.formatString(this.strings.noResultsInRadius, {
            radius,
            location: coords.name,
          });
        }
      }

      // Karte aktualisieren (ein einziger fitBounds-Aufruf)
      if (this.map) {
        this.map.invalidateSize();
        this.addMarkers(this.filteredLocations);

        const bounds = [[coords.lat, coords.lng]];
        this.filteredLocations.forEach(loc => {
          const lat = this.parseCoord(loc.latitude);
          const lng = this.parseCoord(loc.longitude);
          if (this.hasValidCoords(lat, lng)) bounds.push([lat, lng]);
        });
        this.map.closePopup();
        this.map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12, animate: true });
      }

      this.renderList();
      this.updateStatus();
    }

    // --- Geocoding via Nominatim ---
    async geocode(query) {
      const cacheKey = query.toLowerCase();
      if (this.searchCache[cacheKey]) return this.searchCache[cacheKey];

      const cc = this.geocodeCountryCodes();
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}${cc ? `&countrycodes=${cc}` : ''}`;

      try {
        const res = await fetch(url, {
          headers: { 'Accept-Language': document.documentElement.lang || 'de' },
        });
        if (!res.ok) return null;
        const data = await res.json();
        if (!Array.isArray(data) || data.length === 0) return null;

        const coords = {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
          name: data[0].display_name.split(',')[0],
        };
        this.searchCache[cacheKey] = coords;
        return coords;
      } catch (e) {
        return null;
      }
    }

    // --- Länder-Eingrenzung fürs Geocoding aus den Händlerdaten ableiten ---
    geocodeCountryCodes() {
      if (this._countryCodes != null) return this._countryCodes;

      const codes = new Set(['de']);
      let unknown = false;
      this.locations.forEach(loc => {
        const name = this.normalizeText(loc.country);
        if (!name) return;
        if (COUNTRY_CODES[name]) {
          codes.add(COUNTRY_CODES[name]);
        } else {
          unknown = true;
        }
      });

      // Unbekannte Länderbezeichnung → lieber weltweit suchen als Händler ausschließen
      this._countryCodes = unknown ? '' : [...codes].join(',');
      return this._countryCodes;
    }

    // --- Geolocation: Händler in der Nähe des Nutzers ---
    handleGeolocate() {
      const btn = this.querySelector('[data-geolocate-btn]');

      if (!navigator.geolocation) {
        this.showStatusMessage(this.strings.geolocateError);
        return;
      }

      const radius = this.currentRadius();

      if (btn) {
        btn.classList.add('is-loading');
        btn.disabled = true;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (btn) {
            btn.classList.remove('is-loading');
            btn.disabled = false;
          }
          const input = this.querySelector('[data-search-input]');
          if (input) {
            input.value = '';
            input.dispatchEvent(new Event('input'));
          }
          this._searchHint = null;
          this._emptyMessage = null;
          this.applyRadiusSearch(
            { lat: pos.coords.latitude, lng: pos.coords.longitude, name: this.strings.nearYou },
            radius,
            []
          );
          this.focusStatus();
        },
        () => {
          if (btn) {
            btn.classList.remove('is-loading');
            btn.disabled = false;
          }
          this.showStatusMessage(this.strings.geolocateError);
        },
        { timeout: 10000, maximumAge: 300000 }
      );
    }

    // --- Text normalisieren, mit Index-Map zurück zum Original (für Highlighting) ---
    normalizeWithMap(str) {
      const map = [];
      let norm = '';
      for (let i = 0; i < str.length; i++) {
        const n = str[i]
          .toLowerCase()
          .replace(/ß/g, 'ss')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
        for (let j = 0; j < n.length; j++) map.push(i);
        norm += n;
      }
      return { norm, map };
    }

    // --- Vorschlags-Index: Händlernamen + eindeutige Städte ---
    buildSuggestIndex() {
      this._suggestIndex = [];

      this.locations.forEach(loc => {
        const name = (loc.name || '').trim();
        if (!name) return;
        const sub = [String(loc.postal_code || '').trim(), (loc.city || '').trim()]
          .filter(Boolean)
          .join(' ');
        this._suggestIndex.push({
          kind: 'dealer',
          label: name,
          sub,
          subCompact: this.normalizeText(sub).replace(/[\s-]+/g, ''),
          postalCompact: this.normalizeText(String(loc.postal_code || '')).replace(/[\s-]+/g, ''),
          ...this.normalizeWithMap(name),
        });
      });

      const seen = new Set();
      this.locations.forEach(loc => {
        const city = (loc.city || '').trim();
        if (!city) return;
        const key = this.normalizeText(city);
        if (seen.has(key)) return;
        seen.add(key);
        this._suggestIndex.push({
          kind: 'city',
          label: city,
          sub: (loc.country || '').trim(),
          subCompact: '',
          ...this.normalizeWithMap(city),
        });
      });
    }

    // --- Vorschläge filtern und rendern ---
    renderSuggestions(rawQuery) {
      const box = this.querySelector('[data-suggest]');
      const input = this.querySelector('[data-search-input]');
      if (!box || !input) return;

      const q = this.normalizeText((rawQuery || '').trim());
      const qCompact = q.replace(/[\s-]+/g, '');

      if (!q) {
        this.closeSuggestions();
        return;
      }

      // Ranking: Prefix vor Substring vor PLZ-/Ort-Treffer im Untertitel.
      // Reine Ziffern matchen ab 2 Stellen als PLZ-Prefix (Leitregion);
      // sonst Sub-Treffer erst bei längeren Queries, damit kurze
      // Buchstabenfolgen nicht über die Ortsnamen hineinrauschen.
      const isDigits = /^\d+$/.test(qCompact);
      const allowSub = /\d/.test(qCompact) ? qCompact.length >= 3 : qCompact.length >= 4;
      const score = (item) => {
        if (item.norm.startsWith(q)) return 0;
        if (item.norm.includes(q)) return 1;
        if (isDigits && qCompact.length >= 2 && item.postalCompact && item.postalCompact.startsWith(qCompact)) return 1;
        if (allowSub && item.subCompact && item.subCompact.includes(qCompact)) return 2;
        return -1;
      };

      const matched = [];
      this._suggestIndex.forEach(item => {
        const s = score(item);
        if (s >= 0) matched.push({ item, s });
      });
      matched.sort((a, b) => a.s - b.s || a.item.label.localeCompare(b.item.label, this._locale));

      const dealers = matched.filter(m => m.item.kind === 'dealer').slice(0, 5);
      const cities = matched.filter(m => m.item.kind === 'city').slice(0, Math.max(2, 8 - dealers.length));
      const groups = [
        { label: this.strings.suggestDealers, items: dealers },
        { label: this.strings.suggestCities, items: cities },
      ].filter(g => g.items.length > 0);

      if (groups.length === 0) {
        this.closeSuggestions();
        return;
      }

      const icons = {
        dealer: '<svg class="ds-store-locator__suggest-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true"><path d="M4 9 5.5 4h13L20 9"/><path d="M5 9v11h14V9"/><path d="M9.5 20v-5h5v5"/><path d="M4 9h16"/></svg>',
        city: '<svg class="ds-store-locator__suggest-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>',
      };

      this._suggestItems = [];
      let idx = 0;
      let html = '';
      groups.forEach(group => {
        html += `<div class="ds-store-locator__suggest-group" role="presentation">${this.escapeHtml(group.label)}</div>`;
        group.items.forEach(({ item }) => {
          this._suggestItems.push(item);
          html += `
            <div class="ds-store-locator__suggest-option" role="option" id="${this.id}-suggest-${idx}"
              data-suggest-index="${idx}" aria-selected="false">
              ${icons[item.kind]}
              <span class="ds-store-locator__suggest-label">${this.highlightMatch(item, q)}</span>
              ${item.sub ? `<span class="ds-store-locator__suggest-sub">${this.escapeHtml(item.sub)}</span>` : ''}
            </div>`;
          idx++;
        });
      });

      box.innerHTML = html;
      box.hidden = false;
      box.scrollTop = 0;
      this._suggestActive = -1;
      input.setAttribute('aria-expanded', 'true');
      input.removeAttribute('aria-activedescendant');
    }

    // --- Übereinstimmenden Teil im Label hervorheben ---
    highlightMatch(item, q) {
      const start = item.norm.indexOf(q);
      if (!q || start === -1) return this.escapeHtml(item.label);

      const from = item.map[start];
      const to = item.map[start + q.length - 1] + 1;
      return this.escapeHtml(item.label.slice(0, from)) +
        `<mark class="ds-store-locator__suggest-mark">${this.escapeHtml(item.label.slice(from, to))}</mark>` +
        this.escapeHtml(item.label.slice(to));
    }

    setActiveSuggestion(index, options = {}) {
      const { scroll = true } = options;
      const input = this.querySelector('[data-search-input]');
      this._suggestActive = index;

      this.querySelectorAll('[data-suggest-index]').forEach(el => {
        const active = parseInt(el.dataset.suggestIndex, 10) === index;
        el.classList.toggle('is-active', active);
        el.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) {
          if (input) input.setAttribute('aria-activedescendant', el.id);
          if (scroll) el.scrollIntoView({ block: 'nearest' });
        }
      });
    }

    selectSuggestion(index) {
      const item = this._suggestItems[index];
      const input = this.querySelector('[data-search-input]');
      if (!item || !input) return;

      input.value = item.label;
      if (this._updateClearBtn) this._updateClearBtn();
      this.closeSuggestions();
      this.handleSearch({ intent: item.kind === 'dealer' ? 'dealer' : 'place' });
    }

    closeSuggestions() {
      const box = this.querySelector('[data-suggest]');
      const input = this.querySelector('[data-search-input]');
      if (box) {
        box.hidden = true;
        box.innerHTML = '';
      }
      if (input) {
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
      }
      this._suggestItems = [];
      this._suggestActive = -1;
    }

    isSuggestOpen() {
      const box = this.querySelector('[data-suggest]');
      return !!box && !box.hidden && this._suggestItems.length > 0;
    }

    // --- Ladezustand des Such-Buttons ---
    setSearchBusy(busy) {
      const btn = this.querySelector('[data-search-btn]');
      if (btn) {
        btn.classList.toggle('is-loading', busy);
        btn.disabled = busy;
        btn.setAttribute('aria-busy', busy ? 'true' : 'false');
      }
      if (busy) this.showStatusMessage(this.strings.searching);
    }

    showStatusMessage(message) {
      const status = this.querySelector('[data-status]');
      if (status) status.textContent = message;
    }

    // --- Fokus auf Status (Screen Reader Announcement) ---
    focusStatus() {
      const status = this.querySelector('[data-status]');
      if (status) status.focus();
    }

    // --- Haversine-Distanz (km) ---
    haversine(lat1, lon1, lat2, lon2) {
      const R = 6371;
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    // --- Typ-Dropdown ---
    buildFilterChips() {
      const menu = this.querySelector('[data-filters]');
      const types = [...new Set(this.locations.map(l => l.type).filter(Boolean))];
      // Typ-Filter nur anbieten, wenn es mind. 2 Typen gibt
      this.setupDropdown(menu, 'filter', types.length >= 2 ? types : [], 'activeFilter', this.strings.filterAll);
    }

    // --- Generischer Dropdown-Aufbau (Typ + Produkt) ---
    setupDropdown(menu, kind, values, stateKey, allLabel) {
      if (!menu) return;
      const dropdown = menu.closest('[data-dropdown]');

      if (!values || values.length === 0) {
        if (dropdown) dropdown.hidden = true;
        return;
      }

      const labelEl = dropdown.querySelector('[data-dropdown-label]');
      const defaultLabel = labelEl ? labelEl.textContent.trim() : allLabel;
      dropdown._defaultLabel = defaultLabel;

      const check = `<svg class="ds-store-locator__dropdown-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>`;

      const options = [{ value: 'all', label: allLabel }]
        .concat(values.map(v => ({ value: v, label: v })));

      menu.innerHTML = options.map(o => `
        <button type="button" class="ds-store-locator__dropdown-option"
          data-${kind}="${this.escapeHtml(o.value)}" aria-pressed="${o.value === 'all' ? 'true' : 'false'}">
          <span>${this.escapeHtml(o.label)}</span>
          ${check}
        </button>`).join('');

      menu.addEventListener('click', (e) => {
        const btn = e.target.closest(`[data-${kind}]`);
        if (!btn) return;

        const value = btn.dataset[kind];
        this[stateKey] = value;

        menu.querySelectorAll(`[data-${kind}]`).forEach(b => {
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });

        if (labelEl) labelEl.textContent = value === 'all' ? defaultLabel : btn.querySelector('span').textContent;
        dropdown.classList.toggle('is-active', value !== 'all');
        dropdown.open = false;

        this.updateFilterReset();
        this.handleSearch();
      });
    }

    // --- Öffnen/Schließen-Verhalten der Dropdowns ---
    setupDropdownBehavior() {
      const dropdowns = [...this.querySelectorAll('[data-dropdown]')];
      if (dropdowns.length === 0) return;

      // Immer nur ein Dropdown gleichzeitig geöffnet
      dropdowns.forEach(d => {
        d.addEventListener('toggle', () => {
          if (!d.open) return;
          dropdowns.forEach(other => { if (other !== d) other.open = false; });
        });
      });

      // Klick außerhalb schließt offene Dropdowns
      document.addEventListener('click', (e) => {
        dropdowns.forEach(d => {
          if (d.open && !d.contains(e.target)) d.open = false;
        });
      });

      // Escape schließt + Fokus zurück auf den Trigger
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const open = dropdowns.find(d => d.open);
        if (open) {
          open.open = false;
          const summary = open.querySelector('summary');
          if (summary) summary.focus();
        }
      });
    }

    applyTypeFilter(locations) {
      if (this.activeFilter === 'all') return locations;
      return locations.filter(l => l.type === this.activeFilter);
    }

    // --- Produkt-Dropdown ---
    buildProductChips() {
      const menu = this.querySelector('[data-product-filters]');

      // Feste Reihenfolge der BOXIO-Produktlinien, danach evtl. weitere
      const order = ['Toilet', 'Wash', 'Cook', 'Cool', 'Table'];
      const present = new Set();
      this.locations.forEach(l => {
        if (Array.isArray(l.products)) l.products.forEach(p => present.add(p));
      });

      const ordered = order.filter(p => present.has(p));
      present.forEach(p => { if (!ordered.includes(p)) ordered.push(p); });

      this.setupDropdown(menu, 'product', ordered, 'activeProduct', this.strings.filterAllProducts);
    }

    applyProductFilter(locations) {
      if (this.activeProduct === 'all') return locations;
      return locations.filter(l => Array.isArray(l.products) && l.products.includes(this.activeProduct));
    }

    // --- Alle aktiven Filter kombiniert (Typ + Produkt) ---
    applyFilters(locations) {
      return this.applyProductFilter(this.applyTypeFilter(locations));
    }

    // --- Alle Filter zurücksetzen ---
    resetFilters() {
      this.activeFilter = 'all';
      this.activeProduct = 'all';

      [['[data-filters]', 'filter'], ['[data-product-filters]', 'product']].forEach(([sel, kind]) => {
        const menu = this.querySelector(sel);
        if (!menu) return;

        menu.querySelectorAll(`[data-${kind}]`).forEach(b => {
          b.setAttribute('aria-pressed', b.dataset[kind] === 'all' ? 'true' : 'false');
        });

        const dropdown = menu.closest('[data-dropdown]');
        if (dropdown) {
          dropdown.classList.remove('is-active');
          const labelEl = dropdown.querySelector('[data-dropdown-label]');
          if (labelEl && dropdown._defaultLabel) labelEl.textContent = dropdown._defaultLabel;
        }
      });

      this.updateFilterReset();
      this.handleSearch();
    }

    // --- Reset-Button nur zeigen, wenn ein Filter aktiv ist ---
    updateFilterReset() {
      const reset = this.querySelector('[data-filter-reset]');
      if (!reset) return;
      reset.hidden = this.activeFilter === 'all' && this.activeProduct === 'all';
    }

    // --- Filter-Leiste ausblenden, wenn kein Dropdown sichtbar ist ---
    updateFilterbar() {
      const bar = this.querySelector('[data-filterbar]');
      if (!bar) return;
      const anyVisible = [...this.querySelectorAll('[data-dropdown]')].some(d => !d.hidden);
      bar.hidden = !anyVisible;
    }

    // --- Liste rendern ---
    renderList() {
      const list = this.querySelector('[data-list]');
      if (!list) return;

      if (this.filteredLocations.length === 0) {
        list.innerHTML = `<li class="ds-store-locator__empty">${this.escapeHtml(this._emptyMessage || this.strings.noResults)}</li>`;
        this._searchHint = null;
        this._emptyMessage = null;
        return;
      }

      let hint = '';
      if (this._searchHint) {
        hint = `<li class="ds-store-locator__hint">${this.escapeHtml(this._searchHint)}</li>`;
        this._searchHint = null;
      }

      list.innerHTML = hint + this.filteredLocations.map((loc, i) => {
        const distance = Number.isFinite(loc._distance)
          ? `<p class="ds-store-locator__card-distance">${this.formatString(this.strings.distanceAway, { count: Math.round(loc._distance) })}</p>`
          : '';
        const type = loc.type ? `<span class="ds-store-locator__card-type">${this.escapeHtml(loc.type)}</span>` : '';

        let meta = '';
        if (loc.phone) meta += `<a href="tel:${this.escapeHtml(loc.phone)}">${this.escapeHtml(loc.phone)}</a>`;
        if (loc.email) meta += `<a href="mailto:${this.escapeHtml(loc.email)}">${this.escapeHtml(this.strings.email)}</a>`;
        if (loc.website) meta += `<a href="${this.escapeHtml(loc.website)}" target="_blank" rel="noopener">${this.escapeHtml(this.strings.website)}</a>`;
        const cardLat = this.parseCoord(loc.latitude);
        const cardLng = this.parseCoord(loc.longitude);
        if (this.hasValidCoords(cardLat, cardLng)) {
          meta += `<a href="${this.routeUrl(cardLat, cardLng)}" target="_blank" rel="noopener">${this.escapeHtml(this.strings.route)}</a>`;
        }

        const thumbnail = loc.image
          ? `<img class="ds-store-locator__card-thumb" src="${this.escapeHtml(loc.image)}" alt="${this.escapeHtml(loc.name)}" loading="lazy" width="48" height="48">`
          : '';

        const tags = Array.isArray(loc.tags) && loc.tags.length
          ? `<ul class="ds-store-locator__card-tags" aria-label="${this.escapeHtml(this.strings.availableProducts)}">${
              loc.tags.map(tag => `<li class="ds-store-locator__card-tag">${this.escapeHtml(tag)}</li>`).join('')
            }</ul>`
          : '';

        const products = this.renderProducts(loc);
        const hours = this.renderOpeningHours(loc);

        return `
          <li>
            <article class="ds-store-locator__card" tabindex="0" data-card-index="${i}"
              role="button" aria-label="${this.escapeHtml(loc.name)}, ${this.escapeHtml(loc.city)}">
              <div class="ds-store-locator__card-header">
                ${thumbnail}
                <div class="ds-store-locator__card-header-text">
                  <div class="ds-store-locator__card-title-row">
                    <h3 class="ds-store-locator__card-name">${this.escapeHtml(loc.name)}</h3>
                    ${type}
                  </div>
                  <p class="ds-store-locator__card-address">
                    ${this.escapeHtml(loc.address)}<br>
                    ${this.escapeHtml(loc.postal_code)} ${this.escapeHtml(loc.city)}${loc.country ? `<br>${this.escapeHtml(loc.country)}` : ''}
                  </p>
                </div>
              </div>
              ${meta ? `<div class="ds-store-locator__card-meta">${meta}</div>` : ''}
              ${distance}
              ${loc.description && typeof loc.description === 'string' && loc.description.trim() ? `<p class="ds-store-locator__card-description">${this.escapeHtml(loc.description)}</p>` : ''}
              ${products}
              ${tags}
              ${hours}
            </article>
          </li>`;
      }).join('');

      // Card-Klick → Marker auf Karte öffnen
      list.querySelectorAll('[data-card-index]').forEach(card => {
        const handler = () => {
          const idx = parseInt(card.dataset.cardIndex, 10);
          this.highlightLocation(idx);
        };
        card.addEventListener('click', handler);
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handler();
          }
        });
      });

      // Öffnungszeiten-Toggle darf nicht das Karten-Highlight der Card auslösen
      list.querySelectorAll('.ds-store-locator__hours-summary').forEach(summary => {
        summary.addEventListener('click', (e) => e.stopPropagation());
        summary.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
        });
      });
    }

    // --- Produkte rendern (kontrollierte Kategorie) ---
    renderProducts(loc) {
      if (!Array.isArray(loc.products) || loc.products.length === 0) return '';
      return `<ul class="ds-store-locator__card-products" aria-label="${this.escapeHtml(this.strings.products)}">${
        loc.products.map(p => `<li class="ds-store-locator__card-product">${this.escapeHtml(p)}</li>`).join('')
      }</ul>`;
    }

    // --- "Geschlossen"-Erkennung sprachneutral: übersetzte Keywords aus den
    //     Locale-Dateien (closed_keywords) + das übersetzte "Geschlossen"-Label ---
    isClosedText(time) {
      if (!time) return true;
      const t = this.normalizeText(time);
      return String(this.strings.closedKeywords || '')
        .split(',')
        .concat([this.strings.closed || '', 'closed'])
        .map(k => this.normalizeText(k))
        .filter(k => k.length >= 2)
        .some(k => t.includes(k));
    }

    // --- Öffnungszeiten als aufklappbares Dropdown rendern ---
    renderOpeningHours(loc) {
      const raw = loc.opening_hours;
      if (!raw || typeof raw !== 'string' || !raw.trim()) return '';

      const lines = raw.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      if (lines.length === 0) return '';

      const rows = lines.map(line => {
        const idx = line.indexOf(':');
        let day = line;
        let time = '';
        if (idx !== -1) {
          day = line.slice(0, idx).trim();
          time = line.slice(idx + 1).trim();
        }

        const closed = this.isClosedText(time);
        const valueHtml = closed
          ? `<span class="ds-store-locator__hours-closed">${this.escapeHtml(this.strings.closed)}</span>`
          : `<span class="ds-store-locator__hours-time">${this.escapeHtml(time)}</span>`;

        return `<div class="ds-store-locator__hours-row"><span class="ds-store-locator__hours-day">${this.escapeHtml(day)}</span>${valueHtml}</div>`;
      }).join('');

      return `
        <details class="ds-store-locator__hours">
          <summary class="ds-store-locator__hours-summary">
            <svg class="ds-store-locator__hours-icon" viewBox="0 0 24 24" fill="none" stroke-width="1.5" aria-hidden="true">
              <circle cx="12" cy="12" r="9"/>
              <path d="M12 7v5l3 2"/>
            </svg>
            <span>${this.escapeHtml(this.strings.openingHours)}</span>
            <svg class="ds-store-locator__hours-chevron" viewBox="0 0 24 24" fill="none" stroke-width="2" aria-hidden="true">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </summary>
          <div class="ds-store-locator__hours-body">${rows}</div>
        </details>`;
    }

    // --- Standort auf Karte hervorheben ---
    highlightLocation(index, options = {}) {
      const { animate = true, scrollList = false, zoom = 15 } = options;

      // Aktive Card in der Liste markieren (ggf. in den sichtbaren Bereich scrollen)
      this.querySelectorAll('[data-card-index]').forEach(c => {
        const isActive = parseInt(c.dataset.cardIndex, 10) === index;
        c.setAttribute('aria-current', isActive ? 'true' : 'false');
        if (isActive && scrollList && window.matchMedia('(min-width: 990px)').matches) {
          c.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      });

      const marker = this.markers[index];
      if (!marker || !this.map) return;

      // Aktiven Pin hervorheben, vorherigen zurücksetzen
      if (this._activeMarkerIndex !== index) {
        if (this._activeMarkerIndex != null && this.markers[this._activeMarkerIndex]) {
          this.markers[this._activeMarkerIndex].setIcon(this.markerIcon(false));
          this.markers[this._activeMarkerIndex].setZIndexOffset(0);
        }
        marker.setIcon(this.markerIcon(true));
        marker.setZIndexOffset(1000);
        this._activeMarkerIndex = index;
      }

      // Zielposition leicht nach oben versetzt, damit Marker + Popup
      // zusammen optisch mittig in der Karte stehen
      const point = this.map.project(marker.getLatLng(), zoom).subtract([0, 80]);
      const center = this.map.unproject(point, zoom);

      if (!animate) {
        this.map.setView(center, zoom, { animate: false });
        marker.openPopup();
        return;
      }

      // Popup erst nach Ende der Animation öffnen – sonst kämpft das
      // Auto-Panning des Popups gegen die Kartenbewegung (Marker landet am Rand)
      if (this.map.getZoom() === zoom && this.map.getCenter().equals(center, 0.0005)) {
        marker.openPopup();
      } else {
        this.map.closePopup();
        this.map.once('moveend', () => marker.openPopup());
        this.map.flyTo(center, zoom, { duration: 0.6 });
      }
    }

    // --- Status aktualisieren ---
    updateStatus() {
      const status = this.querySelector('[data-status]');
      if (!status) return;

      const count = this.filteredLocations.length;
      const template = count === 0
        ? this.strings.resultsCountZero
        : count === 1
          ? this.strings.resultsCountOne
          : this.strings.resultsCountOther;
      status.textContent = this.formatString(template, { count });
    }

    // --- XSS-Schutz ---
    escapeHtml(str) {
      if (!str) return '';
      if (typeof str !== 'string') str = String(str);
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }

  customElements.define('ds-store-locator', DSStoreLocator);
}
